import { useEffect, useRef, useState } from 'react';

/**
 * IntersectionObserver hook — returns [ref, inView]. `inView` flips true once
 * when the element first crosses `threshold` and stays true afterwards (good
 * for one-shot reveal animations).
 */
export function useInView(options = { threshold: 0.18, rootMargin: '0px 0px -80px 0px' }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current || inView) return;
    const el = ref.current;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        io.disconnect();
      }
    }, options);
    io.observe(el);
    return () => io.disconnect();
  }, [inView, options]);

  return [ref, inView];
}

/**
 * Continuous intersection — returns [ref, ratio] where `ratio` is 0..1 of how
 * much of the element is currently in view. Used for the sticky feature scroll
 * to pick which feature image should be showing.
 */
export function useScrollRatio() {
  const ref = useRef(null);
  const [ratio, setRatio] = useState(0);

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const tick = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // 0 while the top is still below viewport, 1 once the bottom leaves.
      const start = vh;
      const end = -rect.height;
      const raw = (start - rect.top) / (start - end);
      setRatio(Math.max(0, Math.min(1, raw)));
    };
    tick();
    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', tick);
    return () => {
      window.removeEventListener('scroll', tick);
      window.removeEventListener('resize', tick);
    };
  }, []);

  return [ref, ratio];
}
